import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { RequestData, UseDataProps } from './types';
import { useStore } from '30.quickConnect.Stores';
import { DECLARATION } from '10.quickConnect.app/routes/paths';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { errorHandler } from '80.quickConnect.Core/helpers';

const useData = (): UseDataProps => {
  // Tag
  const tag = '10.quickConnect.app/components/domain/Home/Declarations/DeclarationHistory/hooks.ts';

  const {
    DeclarationStore: { getOldDeclaration },
    LoginStore: { signInInfos },
  } = useStore();
  const [forms, setForms] = useState<any>();
  const [detailsDeclaration, setDetailsDeclaration] = useState<any>();
  const [open, setOpen] = useState<{ [key: string]: boolean }>({});

  const navigate = useNavigate();

  // Depuis la fiche d'un équipement, on cherche l'historique de l'équipement plutôt que celui de l'utilisateur
  const { schemaId, entityId } = useParams();
  const searchCriteria: RequestData = useMemo(
    () => ({
      userUPN: signInInfos.userUPN,
      ...(schemaId && entityId ? { schemaId, instanceId: entityId } : {}),
    }),
    [schemaId, entityId, signInInfos.userUPN],
  );

  useEffect(() => {
    const fetch = async () => {
      const res = await getOldDeclaration(searchCriteria);
      if (res) {
        setForms(res.data);
      }
    };
    fetch();
  }, [getOldDeclaration, searchCriteria]);

  const getDeclarationByFormName = useCallback(
    async (formName: string) => {
      const fetch = async () => {
        try {
          const res = await getOldDeclaration({ ...searchCriteria, formName });
          if (res) {
            setDetailsDeclaration(res.data);
          }
        } catch (error) {
          errorHandler(tag, error, 'getDeclarationByFormName');
        }
      };
      fetch();
    },
    [getOldDeclaration, tag, searchCriteria],
  );

  const handleMoveTo = useCallback(
    (formName: string, id: string) => {
      const fetch = async () => {
        try {
          const res = await getOldDeclaration({ userUPN: signInInfos.userUPN, formName, declarationId: id });
          if (res) {
            navigate(DECLARATION.replace(':formId', res.data.declaration.formId).replace(':declarationId', id));
          }
        } catch (error) {
          errorHandler(tag, error, 'handleMoveTo');
        }
      };
      fetch();
    },
    [getOldDeclaration, tag, navigate, signInInfos.userUPN],
  );

  // La liste des anciennes déclarations ne porte pas l'UO de saisie : on la lit dans le détail de la déclaration
  // (OUCode), comme Android, pour le contrôle d'UO de la duplication
  const getDeclarationOuCode = useCallback(
    async (declarationId: string): Promise<string | undefined> => {
      const res = await getOldDeclaration({ userUPN: signInInfos.userUPN, declarationId });
      return res?.data?.declaration?.OUCode;
    },
    [getOldDeclaration, signInInfos.userUPN],
  );

  const handleClick = useCallback(
    (item: string) => {
      getDeclarationByFormName(item);
      setOpen((prevState: { [key: string]: boolean }) => ({ [item]: !prevState[item] }));
    },
    [getDeclarationByFormName],
  );
  return {
    forms,
    getDetailDec: getDeclarationByFormName,
    detailsDeclaration,
    handleMoveTo,
    getDeclarationOuCode,
    setOpen,
    handleClick,
    open,
  };
};
export default useData;
